import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SettingsIcon from '@mui/icons-material/Settings';
import { toast } from 'react-toastify';
import {
  clearAsrosyncSession,
  getConductorTenantId,
  getConductorUserId,
  getConductorUserName,
  setAsrosyncConnectionSettings,
} from '../ChatHub/asrosyncAuth';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogFormContentSx,
  harmonateFormFieldsSx,
  harmonateGridActionIconSx,
  harmonateSectionTitleSx,
  harmonateUploadOutlinedSx,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';

const getBaseUrl = () => process.env.REACT_APP_BACKEND_URL || '';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const parseJsonSafely = async (res) => {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

const getTenantId = () => getConductorTenantId();

const AsroSyncConnect = () => {
  const { canAdd, canEdit, canDelete } = useFunctionPermissions(FC.EXTERNAL_INTEGRATION);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState('DISCONNECTED');
  const [hasSecret, setHasSecret] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({
    clientId: '',
    clientSecret: '',
    authUrl: '',
    apiBaseUrl: '',
    tenantId: '',
  });

  const connected = String(status).toUpperCase() === 'CONNECTED';

  const load = useCallback(async () => {
    const tenantId = getTenantId();
    if (!tenantId) {
      setLoading(false);
      toast.error('Tenant context missing. Please sign in again.');
      return;
    }
    try {
      setLoading(true);
      const res = await fetch(
        `${getBaseUrl()}conductor/asrosync/connection?tenantId=${encodeURIComponent(tenantId)}`,
        { headers: authHeaders() }
      );
      const data = await parseJsonSafely(res);
      if (!res.ok) {
        throw new Error(data?.message || `Failed to load connection (${res.status})`);
      }
      setStatus(data?.status || 'DISCONNECTED');
      setHasSecret(Boolean(data?.hasSecret));
      setForm({
        clientId: data?.clientId || '',
        clientSecret: '',
        authUrl: data?.authUrl || '',
        apiBaseUrl: data?.apiBaseUrl || '',
        tenantId: data?.tenantId || '',
      });
      if (String(data?.status || '').toUpperCase() === 'CONNECTED') {
        setAsrosyncConnectionSettings({
          tenantId: data?.tenantId,
          apiBaseUrl: data?.apiBaseUrl,
        });
      } else {
        clearAsrosyncSession();
      }
    } catch (e) {
      toast.error(e.message || 'Failed to load AsroSync connection');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const validateForm = () => {
    if (!form.clientId.trim()) {
      toast.error('Client ID is required');
      return false;
    }
    if (!form.clientSecret.trim() && !hasSecret) {
      toast.error('Client Secret is required');
      return false;
    }
    if (!form.authUrl.trim()) {
      toast.error('Auth URL is required');
      return false;
    }
    if (!form.tenantId.trim()) {
      toast.error('AsroSync Tenant ID is required');
      return false;
    }
    return true;
  };

  const handleConnect = async () => {
    if (!canAdd && !canEdit) return;
    const tenantId = getTenantId();
    if (!tenantId) {
      toast.error('Tenant context missing');
      return;
    }
    if (!validateForm()) return;

    try {
      setSubmitting(true);
      const res = await fetch(
        `${getBaseUrl()}conductor/asrosync/connect?tenantId=${encodeURIComponent(tenantId)}`,
        {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({
            clientId: form.clientId.trim(),
            clientSecret: form.clientSecret.trim() || undefined,
            authUrl: form.authUrl.trim(),
            apiBaseUrl: form.apiBaseUrl.trim() || undefined,
            tenantId: form.tenantId.trim(),
            connectedUserId:
              getConductorUserId() || getConductorUserName() || undefined,
            connectedUserName: getConductorUserName() || undefined,
          }),
        }
      );
      const data = await parseJsonSafely(res);
      if (!res.ok) {
        throw new Error(data?.message || `Connect failed (${res.status})`);
      }
      setStatus(data?.status || 'CONNECTED');
      setHasSecret(true);
      setForm((prev) => ({ ...prev, clientSecret: '' }));
      setAsrosyncConnectionSettings({
        jwtToken: data?.jwt || data?.accessToken || data?.access_token,
        tenantId: data?.tenantId || data?.targetTenantId || form.tenantId.trim(),
        apiBaseUrl: data?.apiBaseUrl || form.apiBaseUrl.trim(),
        sourceTenantId: data?.sourceTenantId || getTenantId(),
      });
      setEditOpen(false);
      if (data?.tenantLink?.warning) {
        toast.warning(data.tenantLink.warning);
      } else {
        toast.success('Connected to AsroSync');
      }
      await load();
    } catch (e) {
      toast.error(e.message || 'Connect failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!canDelete) return;
    const tenantId = getTenantId();
    if (!tenantId) return;
    try {
      setSubmitting(true);
      const res = await fetch(
        `${getBaseUrl()}conductor/asrosync/disconnect?tenantId=${encodeURIComponent(tenantId)}`,
        { method: 'POST', headers: authHeaders() }
      );
      const data = await parseJsonSafely(res);
      if (!res.ok) {
        throw new Error(data?.message || `Disconnect failed (${res.status})`);
      }
      setStatus('DISCONNECTED');
      clearAsrosyncSession();
      setEditOpen(false);
      toast.success('Disconnected from AsroSync');
      await load();
    } catch (e) {
      toast.error(e.message || 'Disconnect failed');
    } finally {
      setSubmitting(false);
    }
  };

  const openEdit = async () => {
    if (!canEdit) return;
    await load();
    setEditOpen(true);
  };

  const formFields = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, ...harmonateFormFieldsSx }}>
      <TextField
        label="AsroSync Client ID"
        value={form.clientId}
        onChange={(e) => setForm((p) => ({ ...p, clientId: e.target.value }))}
        fullWidth
        size="small"
        required
        autoComplete="off"
      />
      <TextField
        label="AsroSync Client Secret"
        type="password"
        value={form.clientSecret}
        onChange={(e) => setForm((p) => ({ ...p, clientSecret: e.target.value }))}
        fullWidth
        size="small"
        required={!hasSecret}
        autoComplete="off"
        helperText={
          hasSecret
            ? 'Secret already saved. Leave blank to reuse it, or enter a new one.'
            : 'Required'
        }
      />
      <TextField
        label="Auth URL"
        value={form.authUrl}
        onChange={(e) => setForm((p) => ({ ...p, authUrl: e.target.value }))}
        fullWidth
        size="small"
        required
        placeholder="https://<sync-auth-host>/oauth2/token"
      />
      <TextField
        label="API Base URL"
        value={form.apiBaseUrl}
        onChange={(e) => setForm((p) => ({ ...p, apiBaseUrl: e.target.value }))}
        fullWidth
        size="small"
        placeholder="https://<sync-api-host>/"
        helperText="Optional � derived from Auth URL origin if blank"
      />
      <TextField
        label="AsroSync Tenant ID"
        value={form.tenantId}
        onChange={(e) => setForm((p) => ({ ...p, tenantId: e.target.value }))}
        fullWidth
        size="small"
        required
        helperText="Sync application tenant id (not Conductor tenant)"
      />
    </Box>
  );

  const connectButton = (
    <Button
      variant="contained"
      onClick={handleConnect}
      disabled={submitting || !(canAdd || canEdit)}
      startIcon={submitting ? <CircularProgress size={16} sx={{ color: ht.white }} /> : null}
      sx={harmonateProceedButtonSx}
    >
      {submitting
        ? connected
          ? 'Reconnecting�'
          : 'Connecting�'
        : connected
          ? 'Reconnect'
          : 'Connect'}
    </Button>
  );

  if (loading && !connected && !form.clientId) {
    return (
      <Paper
        sx={{ p: 3, minHeight: 280, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'transparent', boxShadow: 'none' }}
        elevation={0}
      >
        <CircularProgress size={28} sx={{ color: ht.text }} />
      </Paper>
    );
  }

  return (
    <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: 'transparent', boxShadow: 'none' }} elevation={0}>
      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 1.5,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
          borderBottom: `1px solid ${ht.line}`,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ ...harmonateSectionTitleSx, fontSize: '1.125rem' }}>
            Connect to AsroSync
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.35, color: ht.text }}>
            Machine credentials for this Conductor tenant (Chat Hub / tenant link).
          </Typography>
        </Box>
        {connected && (
          <Tooltip title="Update connection">
            <IconButton
              size="small"
              onClick={openEdit}
              disabled={!canEdit}
              aria-label="Update AsroSync connection"
              sx={harmonateGridActionIconSx}
            >
              <SettingsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: connected ? 'center' : 'flex-start',
          p: 2.5,
          gap: 2,
        }}
      >
        {connected ? (
          <Box
            sx={{
              textAlign: 'center',
              px: 1,
              py: 2.5,
              borderRadius: ht.cardRadius,
              bgcolor: ht.listBg,
              border: `1px solid ${ht.line}`,
            }}
          >
            <CheckCircleOutlineIcon
              sx={{ fontSize: 42, color: ht.green, mb: 1 }}
            />
            <Typography sx={{ fontWeight: 600, mb: 0.5, color: ht.text }}>
              AsroSync is connected for this tenant
            </Typography>
            <Typography variant="body2" sx={{ mb: 1.5, color: ht.text }}>
              Credentials are saved on the server. Use the gear to update or reconnect.
            </Typography>
            <Chip
              size="small"
              label="Connected"
              sx={{
                fontWeight: 600,
                bgcolor: ht.secondaryTab,
                color: ht.text,
                borderRadius: ht.buttonRadius,
                height: 24,
                '& .MuiChip-label': { px: 1.5 },
              }}
            />
            {form.tenantId ? (
              <Typography
                variant="caption"
                display="block"
                sx={{ mt: 1.25, color: ht.text }}
              >
                Sync tenant ID: {form.tenantId}
              </Typography>
            ) : null}
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 1 }}>
              <Button
                size="small"
                onClick={openEdit}
                disabled={!canEdit}
                startIcon={<SettingsIcon />}
                sx={harmonateProceedButtonSx}
              >
                Manage connection
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={handleDisconnect}
                disabled={submitting || !canDelete}
                sx={harmonateUploadOutlinedSx}
              >
                Disconnect
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            <Typography variant="body2" sx={{ color: ht.text }}>
              Create an API client in AsroSync ? My Integrations, then enter credentials below.
              Client ID and Client Secret are required.
            </Typography>
            {formFields}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>{connectButton}</Box>
          </>
        )}
      </Box>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Update AsroSync connection</DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
          <Typography variant="body2" sx={{ color: ht.text }}>
            Credentials stay on the server for this Conductor tenant. Client ID and Secret are
            required to connect.
          </Typography>
          {formFields}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEditOpen(false)} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          {connectButton}
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default AsroSyncConnect;
